/*
 * Copyright (c) 2020 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */
import { shouldPolyfill as shouldPolyfillPluralRules } from "@formatjs/intl-pluralrules/should-polyfill";
import { shouldPolyfill as shouldPolyfillRelativeTime } from "@formatjs/intl-relativetimeformat/should-polyfill";
import { Settings as LuxonSettings } from "luxon";
import { useEffect, useState } from "react";
import { createIntl, createIntlCache, defineMessages, RawIntlProvider } from "react-intl";
import { useDispatch } from "react-redux";

import Application from "application/Application";
import { useCommonState } from "common/hooks";
import useConfiguration from "common/hooks/useConfiguration";
import { initAnalytics } from "common/analytics";
import { useLocale } from "common/hooks/useLocale";
import { getBaseApiUrl } from "store/api/utils";
import { configurationSlice } from "store/slices/configuration";
import { useAuthConfig } from "common/hooks/useAuthConfigs";

const { applicationTitle } = defineMessages({
    applicationTitle: {
        defaultMessage: "ViaCal",
        id: "application.title",
    },
});

export default function ApplicationInit() {
    const { supportedLocale, lang } = useLocale();
    const cache = createIntlCache();
    const dispatch = useDispatch();

    const { configuration } = useCommonState();
    const [messages, setMessages] = useState();
    const { setBaseApiUrl, setTenantId } = useConfiguration();

    const { authConfig } = useAuthConfig();

    useEffect(() => {
        if (authConfig) {
            dispatch(configurationSlice.actions.setAuthConfiguration(authConfig));
        }
    }, [authConfig, dispatch]);

    useEffect(() => {
        // Init Mixpanel analytics
        initAnalytics();

        // Set API base url
        const appUrl = [window.location.origin, window.location.pathname].join("");
        setBaseApiUrl(getBaseApiUrl(appUrl));

        //Set tenantId
        // Relevant for 1.0 cities and can be deprecated once all cities migrated to 2.0.URL (driver-app-login) QS tenant_id
        const urlParams = new URLSearchParams(window.location.search);
        const tenant = urlParams.get("tenant_id");
        if (tenant) {
            setTenantId(tenant);
        }

        const getLocaleMessages = async () => {
            const messages = await import(
                /* webpackChunkName: "locale/main-[request]" */ `locales/${lang}`
            );
            document.title = messages[applicationTitle.id];
            setMessages(messages.default);

            if (shouldPolyfillPluralRules(lang)) {
                await import(
                    /* webpackChunkName: "main-pluralrules-[request]" */ `@formatjs/intl-pluralrules/locale-data/${lang}`
                );
            }
            if (shouldPolyfillRelativeTime(lang)) {
                await import(
                    /* webpackChunkName: "main-relativetimeformat-[request]" */ `@formatjs/intl-relativetimeformat/locale-data/${lang}`
                );
            }
        };
        getLocaleMessages();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const intl = createIntl(
        {
            defaultLocale: "en",
            locale: supportedLocale,
            messages,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onError: () => {},
        },
        cache
    );

    const timeZone = configuration?.timezone;
    if (timeZone) {
        intl.timeZone = timeZone;
        LuxonSettings.defaultZone = timeZone;
    }

    return (
        <>
            {configuration.baseApiUrl && (
                <RawIntlProvider value={intl}>
                    <Application />
                </RawIntlProvider>
            )}
        </>
    );
}
